import { useCallback, useMemo, useState } from 'react'
import { useFieldArray, useWatch } from 'react-hook-form'

import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import { visuallyHidden } from '@mui/utils'
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table'
import { isEqual } from 'lodash-es'

import { FormField } from '~/common/components'
import { useLanguage } from '~/common/hooks'
import Section from '~/components/Section'
import { useProductPage } from '~/products/hooks/useProductPage'
import { createDimensionsColumns } from '~/products/utils/createDimensionColumns'
import { createStockUnit } from '~/products/utils/stock-unit'
import { getAllPossibleDimensionsOptions } from '~/utils/getAllPossibleDimensionsOptions'
import { makeDimensionOptions } from '~/utils/makeDimensionOptions'
import {
    type ProductFormSchema,
    StockUnitFormSchema,
    useProductFormContext,
} from '../../hooks/useProductForm'
import ProductStockUnitAddDialog from './product-stock-unit-add-dialog'

function useAllPossibleOptions() {
    const { control } = useProductFormContext()
    const dimensions = useWatch({ control, name: 'dimensions' })

    return getAllPossibleDimensionsOptions(dimensions)
}

const cleanSKU = (sku: string) => sku?.trim().replace(/\s+/g, ' ') ?? ''

function useStockUnitsField() {
    const { productInfo } = useProductPage()
    const { locations } = productInfo
    const { control, getValues, setValue } = useProductFormContext()
    const { fields, insert } = useFieldArray({
        control,
        name: 'stock_units',
        keyName: 'key',
        rules: {
            validate: {
                hasSkus: (values) => {
                    const suIndexWithErrors = values.reduce<number[]>((acc, su, index) => {
                        const sku = cleanSKU(su.sku)
                        if (!sku) {
                            acc.push(index)
                        }

                        return acc
                    }, [])

                    return (
                        suIndexWithErrors.length === 0 || gettext('Some stock units contain errors')
                    )
                },
            },
        },
    })

    const allPossibleOptions = useAllPossibleOptions()
    const stockUnitMap = useMemo(() => {
        return fields.reduce((acc, su) => {
            const key = su.dimension_options.map(({ id }) => id?.toString() ?? '').join('|')
            return key ? acc.set(key, su) : acc
        }, new Map<string, (typeof fields)[number]>())
    }, [fields])

    const [activeStockUnits, inactiveOptionsCombos] = useMemo(() => {
        const activeUnits: typeof fields = []
        const inactiveCombos: Array<string | number | Array<string | number>> = []

        // Remove all skus that are no longer in the options
        allPossibleOptions?.forEach((combos) => {
            const comboKey = combos.join('|')
            const stockUnit = stockUnitMap.get(comboKey)

            if (stockUnit) {
                activeUnits.push(stockUnit)
            } else {
                inactiveCombos.push(combos)
            }
        })

        return [activeUnits, inactiveCombos]
    }, [allPossibleOptions, stockUnitMap])

    const findStockUnitIndexByDimensionOptions = useCallback(
        (options: Array<string | number>) => {
            return allPossibleOptions.findIndex((combos) => {
                return isEqual(combos, options)
            })
        },
        [allPossibleOptions]
    )

    const addStockUnit = useCallback(
        (optionIds: Array<string | number>) => {
            const product = getValues()
            const dimensions = getValues('dimensions')
            const dimensionOptions = makeDimensionOptions(optionIds ?? [], dimensions)
            const stockUnit = createStockUnit(dimensionOptions, locations, product)
            const index = findStockUnitIndexByDimensionOptions(optionIds)
            if (index === -1) {
                return
            }

            insert(index, stockUnit)

            const channelProducts = getValues('channel_products')
            const updatedChannelProducts = channelProducts.map((cp) => ({
                ...cp,
                channel_stock_units: [
                    ...cp.channel_stock_units,
                    {
                        price: '0',
                        images: [],
                        placeholder: false,
                        stock_unit: stockUnit,
                        sku: stockUnit.sku,
                        channel_stock_item: { fulfillment_stock_record_options: [] },
                        fulfillment_locations: locations.map((l) => l.location_id),
                    },
                ],
            }))

            setValue('channel_products', updatedChannelProducts)
        },
        [getValues, locations, findStockUnitIndexByDimensionOptions, insert, setValue]
    )

    const removeStockUnit = useCallback(
        (index: number) => {
            setValue(
                'stock_units',
                getValues('stock_units').filter((_, i) => i !== index)
            )

            setValue(
                'channel_products',
                getValues('channel_products').map((cp) => ({
                    ...cp,
                    channel_stock_units: cp.channel_stock_units.filter(
                        (_, csuIndex) => csuIndex !== index
                    ),
                }))
            )
        },
        [getValues, setValue]
    )

    return {
        canEdit: getValues('stock_units_editing_is_enabled'),
        stockUnits: fields,
        activeStockUnits,
        inactiveOptionsCombos,
        addStockUnit,
        removeStockUnit,
    }
}

type StockUnitsTableProps = {
    activeStockUnits: ProductFormSchema['stock_units']
    stockUnits: ProductFormSchema['stock_units']
    dimensions: ProductFormSchema['dimensions']
    canEdit: boolean
    canAddMore: boolean
    onRemove: (index: number) => void
    onAdd: () => void
}

function StockUnitsTable({
    activeStockUnits,
    stockUnits,
    dimensions = [],
    canEdit,
    canAddMore,
    onRemove,
    onAdd,
}: StockUnitsTableProps) {
    const language = useLanguage()

    const findStockUnitIndex = useCallback(
        (activeStockUnitId: string | number) => {
            return stockUnits.findIndex((su) => su.id === activeStockUnitId)
        },
        [stockUnits]
    )

    const columns = useMemo(() => {
        const hasOnlyOne = stockUnits.length === 1
        const columnHelper = createColumnHelper<StockUnitFormSchema>()

        return [
            ...createDimensionsColumns(dimensions, language),
            columnHelper.display({
                id: 'sku',
                header: 'SKU',
                cell: ({ row }) => {
                    return (
                        <FormField
                            fullWidth
                            disabled={!canEdit}
                            name={`stock_units.${findStockUnitIndex(row.original.id)}.sku`}
                            rules={{
                                validate: (value, form) => {
                                    if (!value) {
                                        return gettext('SKU is required')
                                    }

                                    const stockUnits: StockUnitFormSchema[] = form.stock_units
                                    const lastIndex = stockUnits.findLastIndex(
                                        (su) => su.id !== row.original.id && su.sku === value
                                    )

                                    const hasDuplicates = lastIndex > -1
                                    if (hasDuplicates) {
                                        return gettext(
                                            `SKU is already used in stock unit #${lastIndex + 1}`
                                        )
                                    }

                                    return true
                                },
                            }}
                        />
                    )
                },
                size: 200,
            }),
            columnHelper.display({
                id: 'barcode',
                header: gettext('Barcode'),
                cell: ({ row }) => (
                    <FormField
                        fullWidth
                        disabled={!canEdit}
                        name={`stock_units.${row.index}.stock_item.barcode`}
                    />
                ),
                size: 200,
            }),
            columnHelper.display({
                id: 'actions',
                header: () => <span style={visuallyHidden}>{gettext('Actions')}</span>,
                cell: ({ row }) => (
                    <Tooltip title={hasOnlyOne ? gettext('At least 1 SKU is required') : ''}>
                        <span>
                            <IconButton
                                tabIndex={-1}
                                size="small"
                                disabled={hasOnlyOne}
                                onClick={() => onRemove(row.index)}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                ),
                size: 0,
            }),
        ]
    }, [stockUnits.length, language, dimensions, canEdit, findStockUnitIndex, onRemove])

    const table = useReactTable({
        data: activeStockUnits,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => row.id.toString(),
    })

    return (
        <Stack gap={1} sx={{ display: 'inline-flex', maxWidth: '60vw' }}>
            <TableContainer sx={{ maxHeight: 320, overflow: 'auto', position: 'relative' }}>
                <Table stickyHeader size="small" sx={{ width: 'auto' }}>
                    <TableHead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableCell
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        sx={{
                                            width: header.getSize(),
                                        }}
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                  header.column.columnDef.header,
                                                  header.getContext()
                                              )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <TableRow key={row.id}>
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {canAddMore && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button startIcon={<AddIcon />} color="secondary" onClick={onAdd}>
                        {gettext('Add stock unit')}
                    </Button>
                </Box>
            )}
        </Stack>
    )
}

export default function ProductStockUnits() {
    const dimensions: ProductFormSchema['dimensions'] = useWatch({
        name: 'dimensions',
        defaultValue: [],
    })
    const hasDimensions = dimensions.length > 0

    const {
        canEdit,
        stockUnits,
        activeStockUnits,
        inactiveOptionsCombos,
        addStockUnit,
        removeStockUnit,
    } = useStockUnitsField()

    const [isAdding, setIsAdding] = useState(false)

    return (
        <Section header={<Section.Header label={gettext('Stock units')} />} id="stock_units">
            {hasDimensions ? (
                <>
                    <StockUnitsTable
                        activeStockUnits={activeStockUnits}
                        stockUnits={stockUnits}
                        dimensions={dimensions}
                        canEdit={canEdit}
                        canAddMore={inactiveOptionsCombos.length > 0}
                        onRemove={removeStockUnit}
                        onAdd={() => setIsAdding(true)}
                    />

                    <ProductStockUnitAddDialog
                        open={isAdding}
                        dimensionHeaders={[]}
                        inactiveOptionsCombos={inactiveOptionsCombos}
                        onAdd={addStockUnit}
                        onClose={() => setIsAdding(false)}
                    />
                </>
            ) : (
                <Box sx={{ display: 'flex', gridGap: 8 }}>
                    <FormField
                        name="stock_units.0.sku"
                        variant="outlined"
                        size="small"
                        placeholder="SKU"
                        rules={{ required: gettext('SKU is required') }}
                    />
                    <FormField
                        name="stock_units.0.stock_item.barcode"
                        variant="outlined"
                        size="small"
                        placeholder="Barcode"
                    />
                </Box>
            )}
        </Section>
    )
}
